import { useCallback, useMemo, useState } from 'react';
import { ApiError } from '../../../lib/http/ApiError';
import { useFetch } from '../../../hooks/useFetch';
import { useNotification } from '../../notifications/hooks/useNotification';
import { createUsersApi } from '../api/usersApi';
import type { UpdateUserPayload, User } from '../userTypes';

interface UseUpdateUserResult {
  updateUser: (id: string, payload: UpdateUserPayload) => Promise<User | null>;
  loading: boolean;
  error: ApiError | Error | null;
}

export function useUpdateUser(): UseUpdateUserResult {
  const [client, loading] = useFetch();
  const api = useMemo(() => createUsersApi(client), [client]);
  const { notifyError } = useNotification();

  const [error, setError] = useState<ApiError | Error | null>(null);

  const updateUser = useCallback(
    async (id: string, payload: UpdateUserPayload): Promise<User | null> => {
      try {
        setError(null);
        return await api.update(id, payload);
      } catch (err) {
        const e = err as ApiError | Error;
        setError(e);
        if (ApiError.isApiError(e) && e.isServerError) notifyError(e);
        else if (!ApiError.isApiError(e)) notifyError(e);
        return null;
      }
    },
    [api, notifyError],
  );

  return { updateUser, loading, error };
}
